// The one confirmation every paid GEO run goes through (Web Search
// Visibility on /geo-report, and the legacy GEO check on /search), so the two
// entry points cannot drift. It only renders the backend's own read-only
// estimate (GET .../estimate): the exact scope, the searches and prompts that
// will run, and the Martian-credit estimate. The provider's currency cost is
// never estimated; when no estimate is available the cost is said to be
// unknown. The caller owns the single in-flight request.
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { CircleNotch } from '@phosphor-icons/react';

// confirm: { scope: 'workspace' | 'all_workspaces', estimate, loading, error } | null
export function GeoRunConfirmDialog({ confirm, running, onCancel, onConfirm }) {
  const open = !!confirm;
  const est = confirm?.estimate;
  const bulk = confirm?.scope === 'all_workspaces';
  const names = (est?.workspaces || []).map((w) => w.name).filter(Boolean);
  const blocked = est && est.can_run === false;
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !running) onCancel(); }}>
      <DialogContent data-testid="geo-run-confirm" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{bulk ? 'Run checks for all your workspaces?' : 'Run a web search visibility check?'}</DialogTitle>
          <DialogDescription data-testid="geo-run-confirm-operation">
            {confirm?.loading && 'Checking what this run would do…'}
            {est && !bulk && `Martian will run ${est.searches} live Tavily web searches about ${est.workspaces?.[0]?.website || 'this site'} for ${names[0] || 'this workspace'} only, and save the result.`}
            {est && bulk && `Martian will run ${est.searches} live Tavily web searches across ${names.length} workspace${names.length === 1 ? '' : 's'} (${names.join(', ')}) and save each result.${est.skipped_without_website ? ` ${est.skipped_without_website} without a website will be skipped.` : ''}`}
            {confirm?.error && !est && (bulk ? 'Martian will run live Tavily web searches for each of your workspaces that has a website.' : 'Martian will run live Tavily web searches for this workspace.')}
          </DialogDescription>
        </DialogHeader>
        {est?.prompts?.length > 0 && (
          <ol className="list-decimal pl-5 space-y-0.5 text-[13px] text-[var(--text-secondary)]" data-testid="geo-run-confirm-prompts">
            {est.prompts.map((p, i) => <li key={i}>{p}</li>)}
          </ol>
        )}
        <div className="space-y-2 text-[13px] text-[var(--text-secondary)]" data-testid="geo-run-confirm-cost">
          <p className="font-medium text-[var(--text-primary)]">This may use search credits and incur provider cost.</p>
          {est && (
            <p>
              Estimated: <span className="font-semibold text-[var(--text-primary)]">{est.estimated_credits} {est.credit_unit}</span>{' '}
              ({est.credits_used_this_month} of {est.monthly_credit_limit} used this month; {est.credits_reserved || 0} reserved for in-progress runs). {est.cost_note}
            </p>
          )}
          {confirm?.error && !est && (
            <p data-testid="geo-run-confirm-no-estimate">Credit usage couldn't be estimated right now, so the cost of this run is unknown.</p>
          )}
          {blocked && <p className="text-[var(--error)]" data-testid="geo-run-confirm-blocked">{est.blocked_reason}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={running} data-testid="geo-run-cancel">Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={running || confirm?.loading || blocked}
            data-testid="geo-run-confirm-btn"
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white disabled:opacity-50"
          >
            {running ? <CircleNotch size={14} className="animate-spin mr-2" /> : null}
            {running ? 'Running…' : bulk ? 'Run checks' : 'Run check'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
